import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import './AuthContext.css';

const AuthContext = createContext();

// Session timing constants
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_BEFORE_LOGOUT = 60 * 1000; // Show warning 60 seconds before logout

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Modal states
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const countdownTimerRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const [loginLoading, setLoginLoading] = useState(false);

  // Get backend URL
  const getBackendUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
      return process.env.REACT_APP_API_URL || 'http://localhost:5005';
    } else {
      return process.env.REACT_APP_BACKEND_URL || 'https://lgbt-widget-backend.onrender.com';
    }
  };

  // Check if we have a valid token stored
  const getStoredToken = useCallback(() => {
    return localStorage.getItem('adminToken');
  }, []);

  // Check if session is still valid
  const isSessionValid = useCallback(() => {
    const token = getStoredToken();
    const tokenExpiry = localStorage.getItem('adminTokenExpiry');
    
    if (!token || !tokenExpiry) return false;
    
    const now = Date.now();
    return now < parseInt(tokenExpiry);
  }, [getStoredToken]);

  // Initialize auth state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return isSessionValid();
  });

  // Clear all auth data
  const clearAuthData = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenExpiry');
    localStorage.removeItem('adminAuthenticated');
    localStorage.removeItem('adminLoginTime');
  }, []);

  // Logout function
  const logout = useCallback(() => {
    setIsAuthenticated(false);
    clearAuthData();
    setShowInactivityModal(false);
    clearInterval(countdownTimerRef.current);
    clearInterval(checkIntervalRef.current);
  }, [clearAuthData]);

  // Force logout (used when countdown reaches 0)
  const forceLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Refresh the token to extend session
  const refreshToken = useCallback(async () => {
    const token = getStoredToken();
    if (!token) return false;

    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (data.success && data.token) {
        // Store new token and expiry
        localStorage.setItem('adminToken', data.token);
        const expiryTime = Date.now() + SESSION_TIMEOUT;
        localStorage.setItem('adminTokenExpiry', expiryTime.toString());
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }, [getStoredToken]);

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = async () => {
    setShowInactivityModal(false);
    clearInterval(countdownTimerRef.current);
    
    // Refresh the token
    const refreshed = await refreshToken();
    if (!refreshed) {
      // Token refresh failed, force logout
      logout();
    }
  };

  // Verify token with backend
  const verifyToken = useCallback(async () => {
    const token = getStoredToken();
    if (!token) return false;

    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();
      return data.valid === true;
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  }, [getStoredToken]);

  // Check session validity periodically
  useEffect(() => {
    if (!isAuthenticated) return;

    // Initial verification
    verifyToken().then(valid => {
      if (!valid) {
        logout();
      }
    });

    // Check session every 10 seconds
    checkIntervalRef.current = setInterval(() => {
      const tokenExpiry = localStorage.getItem('adminTokenExpiry');
      if (!tokenExpiry) {
        logout();
        return;
      }

      const now = Date.now();
      const expiry = parseInt(tokenExpiry);
      const timeRemaining = expiry - now;

      // Show warning modal if less than WARNING_BEFORE_LOGOUT remaining
      if (timeRemaining <= WARNING_BEFORE_LOGOUT && timeRemaining > 0 && !showInactivityModal) {
        setShowInactivityModal(true);
        const secondsRemaining = Math.ceil(timeRemaining / 1000);
        setCountdown(secondsRemaining);

        // Start countdown
        countdownTimerRef.current = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownTimerRef.current);
              forceLogout();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (timeRemaining <= 0) {
        // Session expired
        logout();
      }
    }, 10000);

    return () => {
      clearInterval(checkIntervalRef.current);
      clearInterval(countdownTimerRef.current);
    };
  }, [isAuthenticated, showInactivityModal, logout, forceLogout, verifyToken]);

  // Login function - calls backend API
  const login = async (password) => {
    setLoginLoading(true);
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (data.success && data.token) {
        // Store the JWT token
        localStorage.setItem('adminToken', data.token);
        
        // Calculate and store expiry time
        const expiryTime = Date.now() + SESSION_TIMEOUT;
        localStorage.setItem('adminTokenExpiry', expiryTime.toString());
        
        // Legacy fields for compatibility
        localStorage.setItem('adminAuthenticated', 'true');
        localStorage.setItem('adminLoginTime', Date.now().toString());
        
        setIsAuthenticated(true);
        setLoginLoading(false);
        return { success: true };
      } else {
        setLoginLoading(false);
        return { 
          success: false, 
          error: data.message || 'Login failed' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginLoading(false);
      return { 
        success: false, 
        error: 'Unable to connect to server. Please try again.' 
      };
    }
  };

  // Get the stored token (for API calls that need auth)
  const getToken = () => {
    return getStoredToken();
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      login, 
      logout, 
      getToken,
      loginLoading 
    }}>
      {children}
      
      {/* Inactivity Warning Modal */}
      {showInactivityModal && (
        <div className="inactivity-modal-overlay">
          <div className="inactivity-modal">
            <div className="modal-icon warning-icon">
              ⚠️
            </div>
            <h3>Session Timeout Warning</h3>
            <p>You've been inactive for a while. For security reasons, you will be automatically logged out in:</p>
            <div className="countdown-display">
              <div className="countdown-circle">
                <svg className="countdown-svg" viewBox="0 0 100 100">
                  <circle
                    className="countdown-circle-bg"
                    cx="50"
                    cy="50"
                    r="45"
                  />
                  <circle
                    className="countdown-circle-progress"
                    cx="50"
                    cy="50"
                    r="45"
                    style={{
                      strokeDasharray: `${2 * Math.PI * 45}`,
                      strokeDashoffset: `${2 * Math.PI * 45 * (1 - countdown / 60)}`
                    }}
                  />
                </svg>
                <div className="countdown-text">
                  <span className="countdown-number">{countdown}</span>
                  <span className="countdown-label">seconds</span>
                </div>
              </div>
            </div>
            <div className="modal-details">
              <p><strong>Why this happens:</strong> To protect your account and sensitive data, we automatically log you out after 30 minutes of inactivity.</p>
              <p><strong>What you can do:</strong> Click "Stay Logged In" to continue your session or "Logout Now" to end your session immediately.</p>
            </div>
            <div className="modal-actions">
              <button 
                className="modal-btn logout-btn"
                onClick={forceLogout}
              >
                Logout Now
              </button>
              <button 
                className="modal-btn stay-btn"
                onClick={handleStayLoggedIn}
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};